import React, { useEffect, useState } from "react";
import { Activity, RefreshCw, ShieldCheck, ShieldAlert, ShieldOff, Ban, KeyRound, UserPlus, UserX, FileEdit, Trash2, LogIn } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACTION_META = {
  "login.success": { label: "Inloggad", Icon: LogIn, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  "login.failed": { label: "Misslyckad inloggning", Icon: ShieldAlert, color: "text-red-700 bg-red-50 border-red-200" },
  "ip.blocked": { label: "IP blockerad (brute-force)", Icon: Ban, color: "text-red-700 bg-red-50 border-red-200" },
  "ip.unblocked": { label: "IP avblockerad", Icon: ShieldOff, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  "password.change": { label: "Bytte sitt lösenord", Icon: KeyRound, color: "text-sky-700 bg-sky-50 border-sky-200" },
  "password.change.failed": { label: "Misslyckat lösenordsbyte", Icon: ShieldAlert, color: "text-red-700 bg-red-50 border-red-200" },
  "password.reset": { label: "Återställde annans lösenord", Icon: KeyRound, color: "text-amber-700 bg-amber-50 border-amber-200" },
  "user.create": { label: "La till användare", Icon: UserPlus, color: "text-violet-700 bg-violet-50 border-violet-200" },
  "user.delete": { label: "Tog bort användare", Icon: UserX, color: "text-red-700 bg-red-50 border-red-200" },
  "site.update": { label: "Uppdaterade hemsidan", Icon: FileEdit, color: "text-sky-700 bg-sky-50 border-sky-200" },
  "menu.delete": { label: "Tog bort maträtt", Icon: Trash2, color: "text-red-700 bg-red-50 border-red-200" },
  "gallery.delete": { label: "Tog bort galleribild", Icon: Trash2, color: "text-red-700 bg-red-50 border-red-200" },
  "event.delete": { label: "Tog bort händelse", Icon: Trash2, color: "text-red-700 bg-red-50 border-red-200" },
};

const FILTERS = [
  { value: "", label: "Alla händelser" },
  { value: "login.success", label: "Lyckade inloggningar" },
  { value: "login.failed", label: "Misslyckade inloggningar" },
  { value: "user.create", label: "Användare tillagd" },
  { value: "user.delete", label: "Användare borttagen" },
  { value: "password.change", label: "Lösenordsbyten" },
  { value: "password.reset", label: "Lösenordsåterställningar" },
  { value: "site.update", label: "Webbplats-uppdateringar" },
];

function formatRelativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just nu";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min sedan`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} tim sedan`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} d sedan`;
  return d.toLocaleDateString("sv-SE");
}

function formatAbsoluteTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("sv-SE", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(true);

  const load = async (selectedFilter) => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (selectedFilter) params.action = selectedFilter;
      const { data } = await api.get("/admin/audit", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Kunde inte ladda aktivitet");
    } finally {
      setLoading(false);
    }
  };

  const loadBlocks = async () => {
    setBlocksLoading(true);
    try {
      const { data } = await api.get("/admin/blocks");
      setBlocks(data || []);
    } catch (err) {
      // Silent fail — blocked IPs is a non-critical secondary panel
      void err;
    } finally {
      setBlocksLoading(false);
    }
  };

  const unblock = async (ip) => {
    if (!window.confirm(`Avblockera ${ip}? Då kan denna IP försöka logga in igen.`)) return;
    try {
      await api.delete(`/admin/blocks/${encodeURIComponent(ip)}`);
      toast.success(`Avblockerade ${ip}`);
      await Promise.all([loadBlocks(), load(filter)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte avblockera");
    }
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  useEffect(() => {
    loadBlocks();
  }, []);

  const failedLoginCount = items.filter((it) => it.action === "login.failed").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl font-black flex items-center gap-3">
            <Activity size={32} /> Aktivitet
          </h1>
          <p className="text-[var(--text-muted)] mt-1">
            Säkerhetslogg över viktiga händelser — inloggningar, användarändringar och innehållsuppdateringar.
            {failedLoginCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-red-700 font-semibold">
                <ShieldAlert size={14} /> {failedLoginCount} misslyckade inloggningar i listan
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => { load(filter); loadBlocks(); }}
          data-testid="audit-refresh"
          className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--border)] hover:border-[var(--text)] px-4 py-2 text-sm font-semibold"
        >
          <RefreshCw size={14} /> Uppdatera
        </button>
      </div>

      {/* Blocked IPs card */}
      <div
        className={`bg-white border rounded-2xl overflow-hidden ${
          blocks.length > 0 ? "border-red-200" : "border-[var(--border)]"
        }`}
        data-testid="blocked-ips-card"
      >
        <div className={`px-6 py-4 border-b ${blocks.length > 0 ? "bg-red-50 border-red-200" : "border-[var(--border)]"}`}>
          <h3 className="font-serif-display text-xl font-bold flex items-center gap-2">
            <Ban size={18} className={blocks.length > 0 ? "text-red-700" : "text-[var(--text-muted)]"} />
            Blockerade IP-adresser
            {blocks.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-red-600 text-white text-xs font-bold">
                {blocks.length}
              </span>
            )}
          </h3>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Auto-blockeras vid 5+ misslyckade inloggningsförsök. Spärren ligger kvar tills du avblockerar den manuellt.
          </p>
        </div>
        {blocksLoading ? (
          <div className="py-8 text-center text-[var(--text-muted)]">Laddar…</div>
        ) : blocks.length === 0 ? (
          <div className="py-8 text-center text-[var(--text-muted)] flex items-center justify-center gap-2">
            <ShieldCheck size={16} className="text-emerald-700" /> Inga blockerade IP-adresser just nu.
          </div>
        ) : (
          <ul className="divide-y divide-red-100">
            {blocks.map((b) => (
              <li key={b.ip} data-testid={`blocked-ip-${b.ip}`} className="px-6 py-4 flex items-center gap-4">
                <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 border border-red-200 grid place-items-center">
                  <Ban size={16} className="text-red-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono font-semibold">{b.ip}</div>
                  <div className="text-xs text-[var(--text-muted)] mt-0.5 flex flex-wrap gap-x-3">
                    <span>{b.attempts} försök</span>
                    {b.last_email && <span>· senast som <strong>{b.last_email}</strong></span>}
                    {b.blocked_at && <span>· blockerad {formatRelativeTime(b.blocked_at)}</span>}
                  </div>
                </div>
                <button
                  onClick={() => unblock(b.ip)}
                  data-testid={`unblock-${b.ip}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[var(--border)] hover:border-emerald-600 hover:text-emerald-700 px-4 py-2 text-sm font-semibold"
                >
                  <ShieldOff size={14} /> Avblockera
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            onClick={() => setFilter(f.value)}
            data-testid={`audit-filter-${f.value || "all"}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              filter === f.value
                ? "bg-[var(--text)] text-white"
                : "bg-white border border-[var(--border)] hover:border-[var(--text)]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <h3 className="font-serif-display text-xl font-bold flex items-center gap-2">
            <ShieldCheck size={18} /> Senaste händelser
          </h3>
          <span className="text-sm text-[var(--text-muted)]">{total} totalt · visar {items.length}</span>
        </div>
        {loading ? (
          <div className="py-12 text-center text-[var(--text-muted)]">Laddar…</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-[var(--text-muted)]">Inga händelser ännu.</div>
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {items.map((it, idx) => {
              const meta = ACTION_META[it.action] || {
                label: it.action,
                Icon: Activity,
                color: "text-stone-700 bg-stone-50 border-stone-200",
              };
              const { Icon } = meta;
              return (
                <li key={`${it.ts}-${idx}`} data-testid={`audit-row-${idx}`} className="px-6 py-4 flex items-start gap-4 hover:bg-[var(--surface-alt)]/40">
                  <div className={`shrink-0 w-10 h-10 rounded-full grid place-items-center border ${meta.color}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{meta.label}</span>
                      {it.target && (
                        <span className="text-sm text-[var(--text-muted)]">
                          · <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">{it.target}</code>
                        </span>
                      )}
                      {!it.success && (
                        <span className="text-xs font-semibold rounded-full bg-red-100 text-red-700 px-2 py-0.5">misslyckad</span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--text-muted)] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      {it.actor_email && <span>av <strong>{it.actor_email}</strong></span>}
                      {it.ip && it.ip !== "unknown" && <span>från {it.ip}</span>}
                      {it.ua && <span className="truncate max-w-md" title={it.ua}>· {it.ua.slice(0, 60)}{it.ua.length > 60 ? "…" : ""}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-xs text-[var(--text-muted)] tabular-nums" title={formatAbsoluteTime(it.ts)}>
                    {formatRelativeTime(it.ts)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
